import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { EMPTY, Observable, catchError, finalize, map, of, take } from 'rxjs';
import { ConfirmDialogComponent } from '../../confirm-dialog/confirm-dialog.component';
import {
  KiAbdeckungAspekt,
  KiAbdeckungErgebnis,
  KiAbdeckungLuecke,
  KiAbdeckungZustand,
  KiAnfrage,
  KiButtonState,
  KiSperrgrund,
} from '../../../models/ki.model';
import { CoverageLinkDraft } from '../../../models/save-full-requirement.model';
import { KiAnfrageService } from '../../../services/ki-anfrage.service';
import { KiRunService } from '../../../services/ki-run.service';
import { RequirementCoverageService } from '../../../services/requirement-coverage.service';
import { FIELD_LENGTHS } from '../../../shared/field-lengths';
import { KiBadgeComponent } from '../ki-badge/ki-badge.component';
import { KiButtonComponent } from '../ki-button/ki-button.component';
import { KiErgebnisAbgelaufenComponent } from '../ki-ergebnis-abgelaufen/ki-ergebnis-abgelaufen.component';
import { KiErsteNutzungComponent } from '../ki-erste-nutzung/ki-erste-nutzung.component';
import { KiFehlerComponent } from '../ki-fehler/ki-fehler.component';
import { KiGesendetComponent } from '../ki-gesendet/ki-gesendet.component';
import { KiLaeuftComponent } from '../ki-laeuft/ki-laeuft.component';
import { KiPanelComponent } from '../ki-panel/ki-panel.component';

function aspekteAus(anfrage: KiAnfrage | null): KiAbdeckungAspekt[] {
  const aspekte = (anfrage?.ergebnis as Partial<KiAbdeckungErgebnis> | null | undefined)?.aspekte;
  return Array.isArray(aspekte) ? aspekte : [];
}

/** Nur eine echte ID wird zum Link: `ergebnis` kommt ungeprüft aus der Antwort. */
function testfallIdVon(aspekt: KiAbdeckungAspekt): string | null {
  return typeof aspekt.testCaseId === 'string' && aspekt.testCaseId ? aspekt.testCaseId : null;
}

/** Ältere gespeicherte Ergebnisse kennen `zustand` nicht; ohne Testfall-ID lässt sich nichts verknüpfen. */
function zustandVon(aspekt: KiAbdeckungAspekt, verknuepft: ReadonlySet<string>): KiAbdeckungZustand {
  const testCaseId = testfallIdVon(aspekt);
  if (aspekt.zustand === 'NICHT_VERKNUEPFT') {
    if (!testCaseId) return 'NICHT_ABGEDECKT';
    return verknuepft.has(testCaseId) ? 'ABGEDECKT' : 'NICHT_VERKNUEPFT';
  }
  if (aspekt.zustand === 'ABGEDECKT' || aspekt.zustand === 'NICHT_ABGEDECKT') return aspekt.zustand;
  return aspekt.abgedeckt ? 'ABGEDECKT' : 'NICHT_ABGEDECKT';
}

function mit<T>(menge: ReadonlySet<T>, wert: T): ReadonlySet<T> {
  return new Set(menge).add(wert);
}

function ohne<T>(menge: ReadonlySet<T>, wert: T): ReadonlySet<T>;
function ohne<K, V>(menge: ReadonlyMap<K, V>, schluessel: K): ReadonlyMap<K, V>;
function ohne<K, V>(menge: ReadonlySet<K> | ReadonlyMap<K, V>, schluessel: K): ReadonlySet<K> | ReadonlyMap<K, V> {
  const kopie = menge instanceof Map ? new Map<K, V>(menge) : new Set<K>(menge as ReadonlySet<K>);
  kopie.delete(schluessel);
  return kopie;
}

interface AbdeckungZeile {
  aspekt: KiAbdeckungAspekt;
  zustand: KiAbdeckungZustand;
  testCaseId: string | null;
}

interface VerknuepfenFehler {
  textKey: string;
  erneutAnbieten: boolean;
}

/** Ohne Recht (403) oder ohne Testfall (404) hilft Wiederholen nicht. */
function verknuepfenFehlerZu(status: number): VerknuepfenFehler {
  return status === 403 || status === 404
    ? { textKey: 'ki.abdeckung.verknuepfenNichtMoeglich', erneutAnbieten: false }
    : { textKey: 'ki.abdeckung.verknuepfenFehler', erneutAnbieten: true };
}

/**
 * KiButton „Abdeckung prüfen“ mit Panel (page-ki.jsx:321-347). Eigener Lauf neben „Testfälle vorschlagen“; das
 * Panel zeigt das Ergebnis des letzten Laufs oder das gespeicherte aus dem Postfach-Absprung `?kiAnfrage`.
 */
@Component({
  selector: 'app-ki-abdeckung',
  imports: [
    ConfirmDialogComponent,
    KiBadgeComponent,
    KiButtonComponent,
    KiErgebnisAbgelaufenComponent,
    KiErsteNutzungComponent,
    KiFehlerComponent,
    KiGesendetComponent,
    KiLaeuftComponent,
    KiPanelComponent,
    MatButtonModule,
    MatIconModule,
    TranslatePipe,
  ],
  providers: [KiRunService],
  templateUrl: './ki-abdeckung.component.html',
  styleUrl: './ki-abdeckung.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KiAbdeckungComponent implements OnInit {
  protected readonly run = inject(KiRunService);
  private readonly anfragen = inject(KiAnfrageService);
  private readonly coverage = inject(RequirementCoverageService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  readonly projectId = input.required<string>();
  /** Fehlt beim Anlegen; dann sperrt der Aufrufer per `sperrgrund`. */
  readonly requirementId = input<string | null>(null);
  readonly anfId = input<string>('');
  readonly anfTitel = input<string>('');
  readonly sperrgrund = input<KiSperrgrund>();
  /** Solange „Testfälle vorschlagen“ läuft, ginge der übergebene Hinweis verloren. */
  readonly entwurfGesperrt = input(false);
  /** Die Prüfung sieht nur den gespeicherten Stand; ungespeicherte Verknüpfungen verfälschten das Ergebnis. */
  readonly ungespeichert = input(false);
  /** Speichert die Anforderung ohne die Seite zu verlassen und meldet, ob es gelang. */
  readonly speichern = input<(() => Observable<boolean>) | null>(null);
  /** Lücke für den Dialog „Testfälle vorschlagen“; der Titel ist auf die Höchstlänge des Hinweises gekürzt. */
  readonly entwurfErstellen = output<KiAbdeckungLuecke>();
  readonly darfVerknuepfen = input(false);
  /** Neue Verknüpfung (201); null bei „bereits verknüpft“ (409), deren Deckungsart das FE nicht kennt. */
  readonly verknuepft = output<CoverageLinkDraft | null>();

  protected readonly panelOffen = signal(false);
  protected readonly rueckfrageOffen = signal(false);
  protected readonly speichertGerade = signal(false);
  private readonly knopf = viewChild.required(KiButtonComponent);
  private readonly gespeichert = signal<KiAnfrage | null>(null);
  private readonly verknuepfteIds = signal<ReadonlySet<string>>(new Set());
  protected readonly sendetIds = signal<ReadonlySet<string>>(new Set());
  protected readonly verknuepfenFehler = signal<ReadonlyMap<string, VerknuepfenFehler>>(new Map());

  protected readonly buttonState = computed<KiButtonState>(() => {
    if (this.sperrgrund()) return 'gesperrt';
    return this.run.zustand().art === 'laeuft' ? 'laeuft' : 'aktiv';
  });
  protected readonly kontext = computed(() => `${this.anfId()} · ${this.anfTitel()}`);
  protected readonly fehlerKey = computed(() => {
    const zustand = this.run.zustand();
    return zustand.art === 'fehler' ? zustand.textKey : null;
  });
  /** Ein neuer Lauf verdrängt das gespeicherte Ergebnis. */
  protected readonly anzeige = computed<KiAnfrage | null>(() => {
    const zustand = this.run.zustand();
    if (zustand.art === 'fertig') return zustand.anfrage;
    return zustand.art === 'bereit' ? this.gespeichert() : null;
  });
  protected readonly zeilen = computed<AbdeckungZeile[]>(() => {
    const verknuepft = this.verknuepfteIds();
    return aspekteAus(this.anzeige()).map((aspekt) => ({
      aspekt,
      zustand: zustandVon(aspekt, verknuepft),
      testCaseId: testfallIdVon(aspekt),
    }));
  });
  protected readonly offenAnzahl = computed(
    () => this.zeilen().filter((zeile) => zeile.zustand !== 'ABGEDECKT').length,
  );
  /** Bis zum Erst-Hinweis bleibt der Lauf „bereit“ und das Panel ohne Inhalt verborgen. */
  protected readonly panelSichtbar = computed(
    () => this.panelOffen() && (this.run.zustand().art !== 'bereit' || this.gespeichert() !== null),
  );

  ngOnInit(): void {
    const anfrageId = this.route.snapshot.queryParamMap.get('kiAnfrage');
    const requirementId = this.requirementId();
    if (!anfrageId || !requirementId) return;
    this.anfragen
      .laden(this.projectId(), anfrageId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (anfrage) => {
          // Das Postfach meldet nur fertige Anfragen; FEHLER oder LAEUFT ergäben ein falsches „0 von 0“.
          if (anfrage.art !== 'ABDECKUNG' || anfrage.kontextId !== requirementId || anfrage.status !== 'FERTIG') {
            return;
          }
          this.gespeichert.set(anfrage);
          this.panelOffen.set(true);
        },
        // Ohne lesbare Anfrage bleibt nur der Button: ein leeres Panel erklärte nichts.
        error: () => undefined,
      });
  }

  protected pruefen(): void {
    if (this.ungespeichert() && this.speichern()) {
      this.rueckfrageOffen.set(true);
      return;
    }
    this.starten();
  }

  protected speichernUndPruefen(): void {
    const speichern = this.speichern();
    if (!speichern || this.speichertGerade()) return;
    this.speichertGerade.set(true);
    speichern()
      .pipe(
        take(1),
        catchError(() => of(false)),
        finalize(() => {
          this.speichertGerade.set(false);
          this.rueckfrageOffen.set(false);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((gespeichert) => {
        if (gespeichert) this.starten();
      });
  }

  protected rueckfrageAbbrechen(): void {
    if (this.speichertGerade()) return;
    this.rueckfrageOffen.set(false);
  }

  private starten(): void {
    const requirementId = this.requirementId();
    if (!requirementId) return;
    this.panelOffen.set(true);
    this.verknuepfteIds.set(new Set());
    this.verknuepfenFehler.set(new Map());
    this.run.starten({
      projectId: this.projectId(),
      art: 'abdeckung',
      kontextTyp: 'REQUIREMENT',
      kontextId: requirementId,
      start: { pfad: `requirements/${encodeURIComponent(requirementId)}/ki/abdeckung`, body: {} },
    });
  }

  /** Speichert sofort, ohne neue Prüfung; „bereits verknüpft“ (409) ist für den Nutzer dasselbe Ergebnis. */
  protected verknuepfen(testCaseId: string): void {
    const requirementId = this.requirementId();
    if (!requirementId || this.sendetIds().has(testCaseId)) return;
    this.sendetIds.update((ids) => mit(ids, testCaseId));
    this.verknuepfenFehler.update((fehler) => ohne(fehler, testCaseId));
    this.coverage
      .link(this.projectId(), requirementId, testCaseId)
      .pipe(
        map((dto): CoverageLinkDraft | null => ({ testCaseId: dto.testCaseId, coverageKind: dto.coverageKind })),
        catchError((fehler: unknown) => {
          const status = fehler instanceof HttpErrorResponse ? fehler.status : 0;
          if (status === 409) return of(null);
          this.verknuepfenFehler.update((alle) => new Map(alle).set(testCaseId, verknuepfenFehlerZu(status)));
          return EMPTY;
        }),
        finalize(() => this.sendetIds.update((ids) => ohne(ids, testCaseId))),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((link) => {
        this.verknuepfteIds.update((ids) => mit(ids, testCaseId));
        this.verknuepft.emit(link);
      });
  }

  protected entwurf(aspekt: KiAbdeckungAspekt): void {
    if (this.entwurfGesperrt()) return;
    // Der Dialog merkt sich beim Öffnen den Fokus; der Knopf der Lücke verschwindet mit dem Panel.
    this.knopf().fokussieren();
    this.panelOffen.set(false);
    this.entwurfErstellen.emit({
      titel: aspekt.titel.slice(0, FIELD_LENGTHS.KI_HINWEIS_MAX),
      kriteriumId: this.kriteriumId(aspekt),
    });
  }

  /** Ältere gespeicherte Ergebnisse kennen das Feld nicht; `ergebnis` kommt ungeprüft aus der Antwort. */
  private kriteriumId(aspekt: KiAbdeckungAspekt): string | null {
    return typeof aspekt.kriteriumId === 'string' && aspekt.kriteriumId ? aspekt.kriteriumId : null;
  }

  /** Im selben Tab: der Guard der Anforderung fragt bei ungespeicherten Änderungen. */
  protected testfallOeffnen(testCaseId: string): void {
    this.panelOffen.set(false);
    this.router.navigate(['/projects', this.projectId(), 'test-cases', testCaseId, 'edit'], {
      queryParams: { from: 'anforderung', anfId: this.requirementId() },
    });
  }
}
